import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import fieldCaptureService from '@services/fieldCapture';
import {
  FieldCaptureBatchKind,
  FieldCapturePullScope,
  FieldCaptureValueSource,
} from '@models/fieldCapture.model';

export const FIELD_CAPTURE_BATCHES_KEY = ['field-capture', 'batches'];
export const FIELD_CAPTURE_LINKS_KEY = ['field-capture', 'links'];
export const FIELD_CAPTURE_QUARANTINE_KEY = ['field-capture', 'quarantine'];
export const FIELD_CAPTURE_OPEN_VISITS_KEY = ['field-capture', 'open-visits'];
export const FIELD_CAPTURE_SERIAL_REVIEW_KEY = ['field-capture', 'serial-review'];

export const useFieldCaptureBatches = (
  organizationId?: string,
  enabled: boolean = !!organizationId,
) =>
  useQuery({
    queryKey: [...FIELD_CAPTURE_BATCHES_KEY, organizationId ?? ''],
    queryFn: () => fieldCaptureService.getBatches(organizationId),
    enabled,
  });

export const useFieldCaptureLinkTable = (
  search?: string,
  organizationId?: string,
  // Super admins must pick an org first; org admins run on their own context.
  enabled: boolean = !!organizationId,
) =>
  useQuery({
    queryKey: [...FIELD_CAPTURE_LINKS_KEY, search ?? '', organizationId ?? ''],
    queryFn: () => fieldCaptureService.getLinkTable(search, organizationId),
    enabled,
    // Links can change outside this client (interstitial crew first-bind,
    // webhook completions) — always refetch on mount/focus, never serve stale.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

export const useFieldCaptureProjects = (organizationId?: string, enabled = true) =>
  useQuery({
    queryKey: ['field-capture', 'projects', organizationId ?? ''],
    queryFn: () => fieldCaptureService.getProjects(organizationId),
    enabled,
  });

/** ATAV program enablement for the CALLER's org (org admins; super-admins skip this). */
export const useFieldCaptureEnabled = (enabled = true) =>
  useQuery({
    queryKey: ['field-capture', 'enabled'],
    queryFn: () => fieldCaptureService.getEnabled(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useFieldCaptureQuarantine = (enabled = true) =>
  useQuery({
    queryKey: FIELD_CAPTURE_QUARANTINE_KEY,
    queryFn: () => fieldCaptureService.getQuarantine(),
    enabled,
  });

export const useUploadFieldCapturePull = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      kind,
      sourcePullDate,
      organizationId,
      pullScope,
    }: {
      file: File;
      kind: FieldCaptureBatchKind;
      sourcePullDate?: string;
      organizationId?: string;
      pullScope?: FieldCapturePullScope;
    }) => fieldCaptureService.uploadPull(file, kind, sourcePullDate, organizationId, pullScope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_BATCHES_KEY });
    },
  });
};

export const useApplyFieldCaptureBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, organizationId }: { batchId: string; organizationId?: string }) =>
      fieldCaptureService.applyBatch(batchId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_BATCHES_KEY });
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_LINKS_KEY });
    },
  });
};

export const useDiscardFieldCaptureBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, organizationId }: { batchId: string; organizationId?: string }) =>
      fieldCaptureService.discardBatch(batchId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_BATCHES_KEY });
    },
  });
};

export const useLinkFieldCaptureStoreToProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      externalKey,
      projectId,
      organizationId,
    }: {
      externalKey: string;
      projectId: string | null;
      organizationId?: string;
    }) => fieldCaptureService.linkStoreToProject(externalKey, projectId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_LINKS_KEY });
    },
  });
};

/**
 * Rebuilds every terminal's prefill URL from the current param set. Needed
 * whenever the params grow — otherwise the only way to adopt a new one is
 * re-uploading the whole pull.
 */
export const useRegenerateFieldCaptureLinks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId }: { organizationId?: string }) =>
      fieldCaptureService.regenerateLinks(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_LINKS_KEY });
    },
  });
};

/**
 * Open visits change as webhooks land, so this refetches like the link table
 * rather than serving a cached answer the office would act on.
 */
export const useFieldCaptureOpenVisits = (
  organizationId?: string,
  enabled: boolean = !!organizationId,
) =>
  useQuery({
    queryKey: [...FIELD_CAPTURE_OPEN_VISITS_KEY, organizationId ?? ''],
    queryFn: () => fieldCaptureService.getOpenVisits(organizationId),
    enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

/** Serial rows awaiting a human. Refetched on mount — the OCR cron moves them behind our back. */
export const useFieldCaptureSerialReview = (
  organizationId?: string,
  enabled: boolean = !!organizationId,
) =>
  useQuery({
    queryKey: [...FIELD_CAPTURE_SERIAL_REVIEW_KEY, organizationId ?? ''],
    queryFn: () => fieldCaptureService.getSerialReview(organizationId),
    enabled,
    refetchOnMount: 'always',
  });

/**
 * Built on demand rather than cached — the report is the day's cut, and a
 * stale one handed to the client is worse than waiting a few seconds.
 */
export const useBrightstarExport = () =>
  useMutation({
    mutationFn: ({
      organizationId,
      completedOnly,
      from,
      to,
    }: {
      organizationId?: string;
      completedOnly?: boolean;
      /** Report-date window as YYYY-MM-DD, inclusive. Omit both for the full file. */
      from?: string;
      to?: string;
    }) => fieldCaptureService.getBrightstarExport(organizationId, completedOnly, from, to),
  });

export const useRunFieldCaptureOcr = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, limit }: { organizationId?: string; limit?: number }) =>
      fieldCaptureService.runOcr(organizationId, limit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_SERIAL_REVIEW_KEY });
    },
  });
};

export const useResolveFieldCaptureSerial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldCaptureValueId,
      value,
      source,
      organizationId,
    }: {
      fieldCaptureValueId: string;
      value: string;
      source: FieldCaptureValueSource;
      organizationId?: string;
    }) =>
      fieldCaptureService.resolveSerial(fieldCaptureValueId, value, source, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_SERIAL_REVIEW_KEY });
    },
  });
};

export const useBackfillFieldCaptureValues = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, dryRun }: { organizationId?: string; dryRun: boolean }) =>
      fieldCaptureService.backfillValues(organizationId, dryRun),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_SERIAL_REVIEW_KEY });
    },
  });
};

export const useResolveQuarantinedSubmission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      externalKey,
      organizationId,
    }: {
      submissionId: string;
      externalKey: string;
      organizationId?: string;
    }) => fieldCaptureService.resolveQuarantined(submissionId, externalKey, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: FIELD_CAPTURE_QUARANTINE_KEY,
      });
      queryClient.invalidateQueries({ queryKey: FIELD_CAPTURE_LINKS_KEY });
    },
  });
};
